import { useState } from 'react';
import { X } from 'lucide-react';
import { LEGAL_URLS } from '../config/legal';
import { recordBillingAck } from '../lib/billingConsent';

interface BillingConsentModalProps {
  kind: 'one_time' | 'subscription';
  planLabel: string;
  amountText: string;
  intervalText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function BillingConsentModal({
  kind,
  planLabel,
  amountText,
  intervalText,
  onConfirm,
  onCancel,
}: BillingConsentModalProps) {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSubscription = kind === 'subscription';
  const interval = intervalText || 'month';

  async function handleAgree() {
    if (!agreed) return;
    setSubmitting(true);
    setError(null);
    const ack = await recordBillingAck();
    if (!ack.ok) {
      setError(ack.error || 'Something went wrong. Please try again.');
      setSubmitting(false);
      return;
    }
    onConfirm();
  }

  const termsLink = (
    <a href={LEGAL_URLS.terms} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">
      Terms of Service
    </a>
  );

  return (
    <div data-testid="billing-consent-modal" className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Confirm your purchase</h2>
          <button onClick={onCancel} disabled={submitting} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50" aria-label="Cancel">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="font-semibold text-gray-900 text-sm mb-1">
              {isSubscription ? `${planLabel} — ${amountText}/${interval}` : `${planLabel} — ${amountText}, one time`}
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              {isSubscription
                ? `Your card will be charged ${amountText} today and then automatically every ${interval} until you cancel. You can cancel anytime from Account Settings to stop future charges.`
                : 'This is a single charge, not a subscription. Project unlocks are non-refundable once your results are unlocked.'}
            </p>
          </div>

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              data-testid="billing-consent-agree"
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
            />
            <span className="text-sm text-gray-600 leading-snug">
              {isSubscription ? (
                <>I have read the recurring billing terms above and authorize TrueLoad to charge my payment method {amountText} per {interval} until I cancel. I agree to the {termsLink}.</>
              ) : (
                <>I authorize a one-time {amountText} charge to my payment method and agree to the {termsLink}.</>
              )}
            </span>
          </label>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        <div className="flex gap-3 p-5 pt-0">
          <button
            data-testid="billing-consent-cancel"
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="billing-consent-confirm"
            onClick={handleAgree}
            disabled={submitting || !agreed}
            className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Processing...
              </>
            ) : (
              'Agree and Continue'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
